import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import GeneralTooltipsTooltipEvents from 'lumen/components/general/tooltips/tooltip-events';
import type { Day, Event, Holiday } from 'lumen/models';

interface HotelBudgetForecastTableDayCellSignature {
  Args: {
    cellData?: {
      day: Day | null;
      hasWarning: boolean;
      value: string;
    };
    column: {
      classNames?: string;
      day: Day | null;
      hasLineClampTwo: boolean;
      value: string;
    };
  };
}

export default class HotelBudgetForecastTableDayCell extends Component<HotelBudgetForecastTableDayCellSignature> {
  @service declare access: Services['access'];

  get day(): Day | undefined {
    return this.args.cellData?.day ?? this.args.column.day ?? undefined;
  }

  get events(): Event[] {
    return this.day?.events ?? [];
  }

  get hasEventsOrHolidays(): boolean {
    if (!this.day || !this.access.hasFeature('app.events')) {
      return false;
    }

    return Boolean(this.day.holidays.length || this.day.events.length);
  }

  get holidays(): Holiday[] {
    return this.day?.holidays ?? [];
  }

  get value(): string {
    return this.args.cellData?.value ?? this.args.column.value;
  }

  <template>
    <div class="flex gap-100 {{@column.classNames}}">
      {{#if @cellData.hasWarning}}
        <PrismIcon @name="warning" @size="small" />
      {{/if}}
      <div
        class={{if @column.hasLineClampTwo "line-clamp-2" "truncate"}}
        title={{this.value}}
      >
        {{this.value}}
      </div>
      <div>
        {{#if this.hasEventsOrHolidays}}
          <PrismIcon @name="calendar-accepted" @size="small" />
          <EmberTooltip
            @delay={{25}}
            @side="right"
            @tooltipClass="base-tooltip day-detail-tooltip"
          >
            <GeneralTooltipsTooltipEvents
              @events={{this.events}}
              @holidays={{this.holidays}}
              @mergeCountryFlags={{true}}
              @title="menu.events"
            />
          </EmberTooltip>
        {{/if}}
      </div>
    </div>
  </template>
}
